/** Pure logic of the catalogue-sync page (backend plan 2026-10-08 §3.3, §3.5, §3.9).
 *
 *  Everything here is driven by the settings registry and the row payloads: labels come from
 *  the registry's `value_labels`, sources from the resolver. The only keys named here are the
 *  generic catalogue keys the page gives a column or a combined control of their own
 *  (`include`, `kind`, `tour.grain`, `tour.name` + `tour.name_template`, `package.mode`,
 *  `itinerary.mode`, `tour.join`); a registry key the page has never heard of still renders in
 *  the settings drawer and the tenant-defaults form, generically. */
import type {
  CatalogueAiPlan,
  CatalogueAiStatus,
  CatalogueOption,
  CatalogueOutput,
  CatalogueRegistry,
  CatalogueRow,
  SettingError,
  SettingSource,
  SettingSpec,
  SettingsPatch,
  SettingValue,
} from '@/api/catalogue';

export const KEY = {
  include: 'include',
  kind: 'kind',
  grain: 'tour.grain',
  name: 'tour.name',
  nameTemplate: 'tour.name_template',
  packageMode: 'package.mode',
  itineraryMode: 'itinerary.mode',
  join: 'tour.join',
} as const;

/** OCTO's option id that is never a booking key (plan §3.3.5). */
export const DEFAULT_OPTION_ID = 'DEFAULT';

export const SOURCE_LABELS: Record<SettingSource, string> = {
  ops: 'Ops',
  tenant_default: 'Tenant default',
  restech_default: 'Restech default',
  inferred: 'Inferred',
};

export const SOURCE_HINTS: Record<SettingSource, string> = {
  ops: 'Set by ops on this product.',
  tenant_default: "The tenant's default (Settings tab).",
  restech_default: "The integration's default.",
  inferred: 'Inferred by triage from the vendor product.',
};

export function sourceLabel(source: string | null | undefined): string {
  return source && source in SOURCE_LABELS ? SOURCE_LABELS[source as SettingSource] : (source ?? '—');
}

/** Registry group → section heading. An unknown group still renders, title-cased. */
const GROUP_LABELS: Record<string, string> = {
  facts: 'Facts (triage)',
  selection: 'Selection',
  package: 'Package',
  tour: 'Tours',
  itinerary: 'Itinerary',
  activities: 'Activities',
  content: 'Content',
};

export function groupLabel(group: string): string {
  if (GROUP_LABELS[group]) return GROUP_LABELS[group];
  const words = group.replace(/[_.-]+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Other';
}

export function specsByKey(registry: CatalogueRegistry | undefined): Map<string, SettingSpec> {
  return new Map((registry?.settings ?? []).map((s) => [s.key, s]));
}

/** Registry entries grouped in registry order (the registry's order is its resolution order). */
export function groupedSpecs(
  specs: readonly SettingSpec[],
): Array<{ group: string; label: string; specs: SettingSpec[] }> {
  const out: Array<{ group: string; label: string; specs: SettingSpec[] }> = [];
  for (const spec of specs) {
    let g = out.find((x) => x.group === spec.group);
    if (!g) {
      g = { group: spec.group, label: groupLabel(spec.group), specs: [] };
      out.push(g);
    }
    g.specs.push(spec);
  }
  return out;
}

/** A value in words, from the registry entry (`value_labels` for an enum). */
export function valueLabel(spec: SettingSpec | undefined, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (spec?.type === 'bool' || typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (spec?.type === 'enum' && typeof value === 'string') {
    const i = spec.values.indexOf(value);
    return (i >= 0 ? spec.value_labels[i] : undefined) ?? value;
  }
  if (spec?.type === 'languages') {
    if (value === 'auto') return 'Auto (every language offered)';
    if (Array.isArray(value)) return value.join(', ');
  }
  if (spec?.type === 'template' && value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** What the restech default is, in words: the value, or the rule that computes it. */
export function defaultLabel(spec: SettingSpec): string {
  if (spec.default !== null && spec.default !== undefined) return valueLabel(spec, spec.default);
  return spec.default_rule || 'computed per product';
}

// --- options, grain ---------------------------------------------------------------------------

export function hasNonDefaultOptions(options: readonly CatalogueOption[]): boolean {
  return options.some((o) => !o.is_default && o.id !== DEFAULT_OPTION_ID);
}

/** The grain selector is offered only where a tour per option is possible: bookings carry
 *  only the product and option ids, and a DEFAULT option is never a booking key. */
export function offersGrainSelector(row: Pick<CatalogueRow, 'options'>): boolean {
  return hasNonDefaultOptions(row.options);
}

/** Why a value cannot be chosen for this product (shown on the disabled choice), or null. */
export function choiceBlockedReason(
  spec: SettingSpec,
  value: string,
  options: readonly CatalogueOption[],
): string | null {
  if (spec.key === KEY.grain && value === 'option' && !hasNonDefaultOptions(options)) {
    return 'Needs options other than DEFAULT — bookings of a DEFAULT-only product carry no option key.';
  }
  return null;
}

export interface Choice {
  value: string;
  label: string;
  blocked: string | null;
}

/** The values of an enum, each blocked with the reason when it cannot apply to a product with
 *  these `options`. Without options (a bulk edit, a tenant default) nothing is blocked: the
 *  server judges each product. */
export function choicesFor(spec: SettingSpec, options?: readonly CatalogueOption[]): Choice[] {
  return spec.values.map((value, i) => ({
    value,
    label: spec.value_labels[i] ?? value,
    blocked: options ? choiceBlockedReason(spec, value, options) : null,
  }));
}

function uniqSorted(values: Iterable<string>): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

/** The option languages (they feed the `tourlanguage` tag; never a tour split). */
export function optionLanguages(row: Pick<CatalogueRow, 'options' | 'languages'>): string[] {
  const langs = row.options.map((o) => o.language).filter((l): l is string => Boolean(l));
  return langs.length ? uniqSorted(langs) : uniqSorted(row.languages);
}

export function optionStartTimes(row: Pick<CatalogueRow, 'options'>): string[] {
  return uniqSorted(row.options.flatMap((o) => o.start_times ?? []));
}

/** `09:00, 14:30 +3` — the first few, then how many more. */
export function compactList(values: readonly string[], max = 3): string {
  if (values.length === 0) return '—';
  const head = values.slice(0, max).join(', ');
  return values.length > max ? `${head} +${values.length - max}` : head;
}

// --- row settings -----------------------------------------------------------------------------

export function settingOf(
  row: Pick<CatalogueRow, 'settings'>,
  key: string,
): SettingValue | undefined {
  return row.settings[key];
}

export function isIncluded(row: Pick<CatalogueRow, 'settings'>): boolean {
  return settingOf(row, KEY.include)?.value === true;
}

export function isRemoved(row: Pick<CatalogueRow, 'removed_ts'>): boolean {
  return Boolean(row.removed_ts);
}

// --- joins (tour.join, plan 2026-10-11 product joins §9) --------------------------------------

/** The vendor id of the product whose tour this one joins (its effective `tour.join`), or null. */
export function joinTargetOf(row: Pick<CatalogueRow, 'settings'>): string | null {
  const v = settingOf(row, KEY.join)?.value;
  return typeof v === 'string' && v ? v : null;
}

/** How a joiner is named: "‹name› (‹vendor id›)" when its row is known, else its vendor id. */
export function joinerLabel(vendorId: string, row?: Pick<CatalogueRow, 'title' | 'name'> | null): string {
  return row ? `${row.title || row.name} (${vendorId})` : vendorId;
}

/** The products of `joinedBy` (a row's `joined_by`, vendor ids) as one phrase — "A", "A and B",
 *  "A, B and C", past `max` "A, …, E and N more" — each named by `nameOf` when it knows the row. */
export function joinersPhrase(
  joinedBy: readonly string[],
  nameOf: (vendorId: string) => Pick<CatalogueRow, 'title' | 'name'> | null | undefined = () => undefined,
  max = 5,
): string {
  const named = joinedBy.slice(0, max).map((v) => joinerLabel(v, nameOf(v)));
  const more = joinedBy.length - named.length;
  if (more > 0) return `${named.join(', ')} and ${more} more`;
  if (named.length < 2) return named.join('');
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
}

/** A settings write refused because products join this one's tour (plan 2026-10-11 product joins
 *  B8b): the change (not synced, another grain, a kind or package import leaving no tour, a tenant
 *  Tours default reaching it) would take away the one tour their bookings land on. */
export const TARGET_IN_USE = 'join_target_in_use';

/** The console's words for a `join_target_in_use` refusal: who joins (`joinedBy`, a row's
 *  `joined_by`, named by `nameOf` when their rows are known), and what to do first. Null when
 *  `joinedBy` is empty: the server's own detail names them then. */
export function targetInUseDetail(
  joinedBy: readonly string[],
  nameOf?: (vendorId: string) => Pick<CatalogueRow, 'title' | 'name'> | null | undefined,
  whose = "this product's",
): string | null {
  const n = joinedBy.length;
  if (!n) return null;
  return (
    `${joinersPhrase(joinedBy, nameOf)} ${n === 1 ? 'joins' : 'join'} ${whose} tour: clear ` +
    `${n === 1 ? 'its join' : 'their joins'} first. This change would take away the one tour ` +
    `${n === 1 ? 'its' : 'their'} bookings land on.`
  );
}

/** `errors` with each `join_target_in_use` reworded by `targetInUseDetail` for the product it
 *  names (`joinedByOf(product_id)`); the others, and one whose joiners are unknown, unchanged. */
export function explainTargetInUse(
  errors: readonly SettingError[],
  joinedByOf: (productId: number) => readonly string[] | undefined,
  nameOf?: (vendorId: string) => Pick<CatalogueRow, 'title' | 'name'> | null | undefined,
): SettingError[] {
  return errors.map((e) => {
    if (e.code !== TARGET_IN_USE || e.product_id == null) return e;
    const detail = targetInUseDetail(joinedByOf(e.product_id) ?? [], nameOf);
    return detail ? { ...e, detail } : e;
  });
}

const GRAIN_WORDS: Record<string, string> = { none: 'builds no tour', option: 'builds one tour per option' };

/** Why `row` cannot be the product another one joins, from what the row says (the save checks
 *  again, with the database): it must be listed, included, one tour for the product, and join no
 *  third product itself. Null when nothing on the row rules it out. */
export function joinTargetBlockedReason(
  row: Pick<CatalogueRow, 'settings' | 'removed_ts'>,
): string | null {
  const chained = joinTargetOf(row);
  if (chained) return `joins ${chained} itself`;
  if (row.removed_ts) return 'removed upstream';
  if (!isIncluded(row)) return 'not synced: include it first';
  const grain = settingOf(row, KEY.grain)?.value;
  if (typeof grain === 'string' && grain !== 'product') return GRAIN_WORDS[grain] ?? `Tours: ${grain}`;
  return null;
}

// --- outputs and coupling ---------------------------------------------------------------------

/** Outputs that can be decoupled on their own: an alias follows its tour. */
export function couplableOutputs(
  outputs: readonly CatalogueOutput[],
  entities?: readonly string[],
): CatalogueOutput[] {
  return outputs.filter(
    (o) => o.entity !== 'alias' && (entities === undefined || entities.includes(o.entity)),
  );
}

/** The produced tours and itinerary cards — the grid's coupling toggles. */
export function producedOutputs(outputs: readonly CatalogueOutput[]): CatalogueOutput[] {
  return couplableOutputs(outputs, ['tour', 'itinerary']);
}

export const ENTITY_LABELS: Record<string, string> = {
  tour: 'Tour',
  alias: 'Alias',
  itinerary: 'Itinerary',
  activity: 'Activity',
  tag: 'Tag',
};

export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? entity;
}

/** The output ids a bulk (de/re)couple acts on: couplable outputs of the given entities that
 *  are not already in the requested state. */
export function bulkCouplingTargets(
  rows: readonly Pick<CatalogueRow, 'outputs'>[],
  entities: readonly string[],
  coupled: boolean,
): number[] {
  return rows.flatMap((r) =>
    couplableOutputs(r.outputs, entities)
      .filter((o) => o.coupled !== coupled)
      .map((o) => o.id),
  );
}

// --- packages ---------------------------------------------------------------------------------

/** What ops must know about a package under its current mode (plan §3.5). */
export function packageNotes(kind: string | null | undefined, mode: string | null | undefined): string[] {
  const notes: string[] = [];
  if (mode === 'legs') {
    notes.push(
      'Legs import as separate bookings: the package gets no tour, so its non-lodging legs need tours of their own.',
    );
  } else if (mode === 'whole') {
    if (kind === 'package_multiday_flexible') {
      notes.push(
        "Guests choose the date of each part: the itinerary has one day per leg, but real bookings' dates and order vary.",
      );
    } else if (kind && !kind.startsWith('package_')) {
      notes.push(`Imported whole, but triaged as ${kind}: check the product kind.`);
    }
  }
  return notes;
}

export const STICKY_HINT =
  'Once bookings of a package exist, each keeps the import it was first made with (whole or legs); a change reaches new bookings only.';

// --- settings drafts --------------------------------------------------------------------------

/** Unsaved changes of one product: `null` = reset to the default (drop the ops override). */
export interface SettingsDraft {
  product: Record<string, unknown>;
  options: Record<string, Record<string, unknown>>;
}

export const EMPTY_DRAFT: SettingsDraft = { product: {}, options: {} };

export function draftHas(draft: SettingsDraft, key: string, optionId?: string | null): boolean {
  const scope = optionId ? draft.options[optionId] : draft.product;
  return !!scope && Object.prototype.hasOwnProperty.call(scope, key);
}

export function draftGet(draft: SettingsDraft, key: string, optionId?: string | null): unknown {
  return (optionId ? draft.options[optionId] : draft.product)?.[key];
}

export function draftSet(
  draft: SettingsDraft,
  key: string,
  optionId: string | null | undefined,
  value: unknown,
): SettingsDraft {
  if (optionId) {
    return {
      ...draft,
      options: { ...draft.options, [optionId]: { ...draft.options[optionId], [key]: value } },
    };
  }
  return { ...draft, product: { ...draft.product, [key]: value } };
}

/** Drop a pending change (back to what is saved). */
export function draftUnset(
  draft: SettingsDraft,
  key: string,
  optionId?: string | null,
): SettingsDraft {
  if (optionId) {
    const rest = { ...draft.options[optionId] };
    delete rest[key];
    const options = { ...draft.options };
    if (Object.keys(rest).length) options[optionId] = rest;
    else delete options[optionId];
    return { ...draft, options };
  }
  const product = { ...draft.product };
  delete product[key];
  return { ...draft, product };
}

export function draftCount(draft: SettingsDraft): number {
  return (
    Object.keys(draft.product).length +
    Object.values(draft.options).reduce((n, o) => n + Object.keys(o).length, 0)
  );
}

/** The PATCH body of a draft (all or nothing on the server). */
export function patchOf(draft: SettingsDraft, note: string): SettingsPatch {
  const body: SettingsPatch = {};
  if (Object.keys(draft.product).length) body.settings = { ...draft.product };
  const options = Object.fromEntries(
    Object.entries(draft.options).filter(([, v]) => Object.keys(v).length > 0),
  );
  if (Object.keys(options).length) body.options = options;
  if (note.trim()) body.note = note.trim();
  return body;
}

// --- errors -----------------------------------------------------------------------------------

/** The form field a refusal belongs to: `key`, or `<option id>::<key>` for an option key. */
export function fieldKey(key: string, optionId?: string | null): string {
  return optionId ? `${optionId}::${key}` : key;
}

/** 400 refusals mapped onto the drawer's fields; refusals without a key stay general. */
export function mapSettingErrors(errors: readonly SettingError[]): {
  fields: Record<string, string>;
  general: string[];
} {
  const fields: Record<string, string> = {};
  const general: string[] = [];
  for (const e of errors) {
    if (e.key) {
      const k = fieldKey(e.key, e.option_id);
      fields[k] = fields[k] ? `${fields[k]} ${e.detail}` : e.detail;
    } else {
      general.push(e.detail);
    }
  }
  return { fields, general };
}

/** Bulk refusals as lines naming the product (`errors[].product_id`). */
export function bulkErrorLines(
  errors: readonly SettingError[],
  nameOf: (productId: number) => string | undefined,
): string[] {
  return errors.map((e) => {
    const who = e.product_id != null ? (nameOf(e.product_id) ?? `#${e.product_id}`) : null;
    return who ? `${who}: ${e.detail}` : e.detail;
  });
}

// --- names ------------------------------------------------------------------------------------

/** The template field matters only in template mode (and must be set together with it). */
export function needsTemplate(mode: unknown): boolean {
  return mode === 'template';
}

// --- small formatters -------------------------------------------------------------------------

export function formatCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return '—';
  return usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`;
}

export type Tone = 'default' | 'success' | 'warning' | 'error' | 'info';

export function syncStatusTone(status: string | null | undefined): Tone {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (['ok', 'success', 'completed', 'imported', 'done'].includes(s)) return 'success';
  // `join_refused`: a product that joins another's tour, whose IDs the target's import could not write.
  if (['failed', 'error', 'join_refused'].includes(s)) return 'error';
  if (['partial', 'truncated', 'budget', 'skipped'].includes(s)) return 'warning';
  return 'default';
}

export function pendingTone(status: string | null | undefined): Tone {
  if (status === 'pending') return 'warning';
  if (status === 'applied') return 'success';
  return 'default';
}

// ---------------------------------------------------------------------------------------
// The itinerary planner's status (`row.ai`): the latest run, and the latest accepted plan.
// ---------------------------------------------------------------------------------------

const AI_STATUS_WORDS: Record<CatalogueAiStatus['status'], string> = {
  ok: 'ok',
  failed: 'failed',
  truncated: 'truncated',
  budget: 'over budget',
};

/** The latest planner run in a word or two: its outcome, or, when it succeeded, whether its plan
 *  was imported. */
export function aiRunLabel(ai: CatalogueAiStatus): string {
  if (ai.status === 'ok') return ai.accepted ? 'accepted' : 'not accepted';
  return AI_STATUS_WORDS[ai.status];
}

export function aiRunTone(ai: CatalogueAiStatus): Tone {
  if (ai.status === 'ok') return ai.accepted ? 'success' : 'default';
  return syncStatusTone(ai.status);
}

export interface AiPlanFlag {
  key: 'merged_stops' | 'season_notes' | 'dropped_times';
  count: number;
  /** Chip-sized: "2 stops merged". */
  label: string;
  /** What the count means, for the tooltip. */
  help: string;
}

const AI_PLAN_FLAGS: Array<{ key: AiPlanFlag['key']; label: (n: number) => string; help: string }> = [
  {
    key: 'merged_stops',
    label: (n) => `${n} stop${n === 1 ? '' : 's'} merged`,
    help: 'Stops the model gave a slot of their own (no prose cites them), folded into the nearest prose slot.',
  },
  {
    key: 'season_notes',
    label: (n) => `${n} season note${n === 1 ? '' : 's'}`,
    help: 'Slots carrying a season note ("In winter: …").',
  },
  {
    key: 'dropped_times',
    label: (n) => `${n} time${n === 1 ? '' : 's'} dropped`,
    help: 'Prose clock times dropped from slots: not stated by a cited unit, or running the day backwards (those slots show a synthetic time).',
  },
];

/** The accepted plan's flags worth a look: each counter above zero. A null counter is a run older
 *  than the counter and says nothing; no plan, nothing. */
export function aiPlanFlags(plan: CatalogueAiPlan | null): AiPlanFlag[] {
  if (!plan) return [];
  return AI_PLAN_FLAGS.flatMap((f) => {
    const count = plan[f.key];
    return count ? [{ key: f.key, count, label: f.label(count), help: f.help }] : [];
  });
}
